import { useCallback, useEffect, useState } from "react";
import { Copy, Mail, Ban, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError, EMAIL_RE } from "../lib/api";
import { useSeo } from "../components/site/Layout";
import { euros } from "../data/site";
import { fmtDateTime } from "./AdminLayout";
import { PayBadge } from "./AdminPayments";

const EMPTY = { client_name: "", email: "", amount_eur: "", reference: "", description: "", expires_on: "" };
const AMOUNT_MSG = "Le montant doit être supérieur à 0 € et ne pas dépasser 50 000 €.";

const validate = (f) => {
  const e = {};
  if (f.client_name.trim().length < 2) e.client_name = "Nom du client requis.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Email invalide.";
  const a = Number(String(f.amount_eur).replace(",", "."));
  if (!f.amount_eur || Number.isNaN(a) || a <= 0 || a > 50000) e.amount_eur = AMOUNT_MSG;
  if (f.reference.trim().length < 2) e.reference = "Référence du devis requise.";
  if (f.description.trim().length < 2) e.description = "Description requise.";
  return e;
};

const In = ({ id, label, f, setF, errors, ...rest }) => (
  <label className="block"><span className="font-semibold text-navy">{label}</span>
    <input className={`field-input mt-2 ${errors[id] ? "field-error" : ""}`} value={f[id]} onChange={(e) => setF({ ...f, [id]: e.target.value })} data-testid={`link-${id}`} aria-invalid={errors[id] ? "true" : "false"} {...rest} />
    {errors[id] && <span role="alert" className="block mt-1 text-red-700 font-medium" data-testid={`link-error-${id}`}>{errors[id]}</span>}
  </label>
);

export default function AdminLinks() {
  useSeo("Liens de paiement — admin");
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [rows, setRows] = useState(null);
  const load = useCallback(() => adminApi.get("/payment-links").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const url = (t) => `${window.location.origin}/paiement/${t}`;

  const create = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length) return;
    try {
      await adminApi.post("/payment-links", { ...f, amount_eur: Number(String(f.amount_eur).replace(",", ".")), expires_on: f.expires_on || null });
      toast.success("Lien de paiement créé"); setF(EMPTY); load();
    } catch (err) { setErrors({ amount_eur: formatApiError(err.response?.data?.detail) }); }
  };
  const copy = async (t) => {
    try { await navigator.clipboard.writeText(url(t)); toast.success("Lien copié"); }
    catch { window.prompt("Copiez le lien :", url(t)); }
  };
  const act = async (id, path, okMsg) => {
    try { const r = await adminApi.post(`/payment-links/${id}/${path}`); if (r.data.ok === false) toast.error("Échec de l'envoi (consigné)"); else toast.success(okMsg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-links">
      <h1 className="text-3xl sm:text-4xl">Liens de paiement (acomptes)</h1>
      <form noValidate onSubmit={create} className="mt-6 rounded-2xl bg-white border border-slate-200 p-6 grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="link-create-form">
        <In id="client_name" label="Client" f={f} setF={setF} errors={errors} />
        <In id="email" label="Email du client" type="email" f={f} setF={setF} errors={errors} />
        <In id="amount_eur" label="Montant TTC (€)" inputMode="decimal" f={f} setF={setF} errors={errors} />
        <In id="reference" label="Référence devis" placeholder="DEV-2026-001" f={f} setF={setF} errors={errors} />
        <In id="description" label="Description" f={f} setF={setF} errors={errors} />
        <In id="expires_on" label="Expiration (facultatif)" type="date" f={f} setF={setF} errors={errors} />
        <div className="md:col-span-2 xl:col-span-3"><button type="submit" className="btn-primary" data-testid="link-create-submit"><Plus className="h-4 w-4" aria-hidden="true" /> Créer le lien</button></div>
      </form>
      <div className="mt-8 space-y-4">
        {rows?.map((p) => (
          <article key={p.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`link-row-${p.id}`}>
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <p className="font-semibold text-navy">{p.reference} — {p.client_name} <span className="text-slate-600 font-normal">({p.email})</span></p>
                <p className="text-sm text-slate-600">{p.description} · créé le {fmtDateTime(p.created_at)}{p.expires_at && ` · expire le ${fmtDateTime(p.expires_at)}`}</p>
              </div>
              <div className="flex items-center gap-3"><span className="font-display text-2xl text-navy">{euros(p.amount_cents)}</span><PayBadge status={p.status} testid={`link-status-${p.id}`} /></div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <code className="text-xs bg-slate-100 rounded-lg px-3 py-2 break-all max-w-full" data-testid={`link-url-${p.id}`}>{url(p.token)}</code>
              <button type="button" onClick={() => copy(p.token)} className="btn-outline px-4 py-2 text-sm" data-testid={`link-copy-${p.id}`}><Copy className="h-4 w-4" aria-hidden="true" /> Copier le lien</button>
              <a href={url(p.token)} target="_blank" rel="noreferrer" className="btn-outline px-4 py-2 text-sm" data-testid={`link-open-${p.id}`}><ExternalLink className="h-4 w-4" aria-hidden="true" /> Ouvrir</a>
              {["en attente", "échoué"].includes(p.status) && <>
                <button type="button" onClick={() => act(p.id, "send-email", "Email envoyé")} className="btn-primary px-4 py-2 text-sm" data-testid={`link-email-${p.id}`}><Mail className="h-4 w-4" aria-hidden="true" /> Envoyer par email</button>
                <button type="button" onClick={() => act(p.id, "cancel", "Lien annulé")} className="btn px-4 py-2 text-sm text-red-800 border-2 border-red-200 hover:bg-red-50" data-testid={`link-cancel-${p.id}`}><Ban className="h-4 w-4" aria-hidden="true" /> Annuler</button>
              </>}
            </div>
            {p.emails?.length > 0 && <ul className="mt-3 text-sm text-slate-600" data-testid={`link-email-log-${p.id}`}>{p.emails.map((e, i) => <li key={i}>{e.kind} → {e.to} : <strong className={e.status === "envoyé" ? "text-green-800" : "text-red-800"}>{e.status}</strong> ({fmtDateTime(e.at)})</li>)}</ul>}
          </article>
        ))}
        {rows && !rows.length && <p className="text-slate-600" data-testid="links-empty">Aucun lien de paiement.</p>}
      </div>
    </div>
  );
}
